import { ethers } from 'ethers';
import { getInstalledMetamaskProvider } from '@/composables/metamask/useMetamaskProvider';
import {
  getNativeTokenDecimal,
  isNativeCoin,
} from '@/composables/metamask/metamaskHelper';

class UserTransferCoin {
  /**
   * Initialize TransferCoin
   * @param {string} chainId
   * @param {string} coin
   * @param {string} destinationAddress
   * @param {string} amount
   * @param {string} tokenAddress
   */
  constructor(chainId, coin, destinationAddress, amount, tokenAddress, orderKey) {
    this.chainId = chainId;
    this.coin = coin;
    this.destinationAddress = destinationAddress;
    this.amount = amount;
    this.tokenAddress = tokenAddress;
    this.ERC20Abi = [
      'function name() view returns (string)',
      'function symbol() view returns (string)',
      'function balanceOf(address) view returns (uint)',
      'function transfer(address to, uint amount)',
      'function decimals() view returns (uint)',
      'event Transfer(address indexed from, address indexed to, uint amount)',
    ];
    this.orderKey = ethers.utils.id(orderKey).substring(2);
  }

  /**
   * Get non-native coin transaction hash
   *
   * @returns {string|boolean}
   */
  async erc20() {
    try {
      const ethSigner = await this.getProviderSigner();
      const erc20 = new ethers.Contract(
        this.tokenAddress,
        this.ERC20Abi,
        ethSigner,
      );
      const decimal = await erc20.decimals();
      const unsignedTransaction = await erc20.populateTransaction.transfer(
        this.destinationAddress,
        ethers.utils.parseUnits(this.amount, decimal),
      );
      const transaction = await ethSigner.sendTransaction({
        data: unsignedTransaction.data + this.orderKey,
        to: unsignedTransaction.to,
      });
      await transaction.wait();
      return transaction.hash;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  /**
   * Get native coin transaction hash
   *
   * @param {string|null} contractAddress
   * @returns {string|boolean}
   */
  async nativeCoin() {
    try {
      const ethSigner = await this.getProviderSigner();
      const transaction = await ethSigner.sendTransaction({
        to: this.destinationAddress,
        data: `0x${this.orderKey}`,
        value: ethers.utils.parseUnits(
          this.amount,
          getNativeTokenDecimal(parseInt(this.chainId, 16)),
        ),
      });
      await transaction.wait();
      return transaction.hash;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get provider signer
   *
   * @returns {Web3Provider}
   */
  /* eslint-disable class-methods-use-this */
  async getProviderSigner() {
    const mmProvider = await getInstalledMetamaskProvider();
    const ethersWeb3Provider = new ethers.providers.Web3Provider(
      mmProvider,
    );
    const ethSigner = ethersWeb3Provider.getSigner(
      ethersWeb3Provider.provider.selectedAddress,
    );

    return ethSigner;
  }

  /**
   * Transfer coin
   *
   * @returns {string|boolean}
   */
  async transfer() {
    if (!isNativeCoin(this.coin, parseInt(this.chainId, 16))) {
      return this.erc20();
    }
    return this.nativeCoin();
  }
}

export default UserTransferCoin;
